import { nextTick } from 'vue'
import Cookies from 'js-cookie'
import { createI18n } from 'vue-i18n'

import en from '../locales/en.json'
import EventBusV3 from '../shared/eventBus'
import { LOCALE_CHANGED } from '../shared/eventContract'

const localeLoaders = import.meta.glob(['../locales/*.json', '!../locales/en.json'])
const supportedLocales = ['en', ...Object.keys(localeLoaders).map((path) => path.match(/([\w-]+)\.json$/)[1])]

// Same source order as the Vue 2 plugin: the zendrop-lang cookie is what the
// locale selector writes; <html lang> is the server-rendered fallback.
function resolveLocale() {
  const raw = Cookies.get('zendrop-lang') || document.documentElement.lang || 'en'
  return supportedLocales.includes(raw) ? raw : 'en'
}

const locale = resolveLocale()

// Hosted-mode fast path: the Vue 2 plugin may already have published loaded
// messages for the current locale.
const published = window?.config?.locales_terms ?? {}

const i18n = createI18n({
  legacy: false,
  locale,
  fallbackLocale: 'en',
  messages: {
    en,
    ...(locale !== 'en' && published[locale] ? { [locale]: published[locale] } : {}),
  },
})

// Publish for the Vue 2 side — the inverse of today's hosted flow, needed
// once Vue 2 runs as an island inside the Vue 3 shell (Phase 3).
function publishLocaleTerms() {
  window.config = window.config || {}
  window.config.locales_terms = {
    ...(window.config.locales_terms || {}),
    ...i18n.global.messages.value,
  }
}

publishLocaleTerms()

function loadLocaleMessages(target) {
  if (i18n.global.availableLocales.includes(target)) {
    return Promise.resolve()
  }
  return localeLoaders[`../locales/${target}.json`]?.().then((module) => {
    i18n.global.setLocaleMessage(target, module.default ?? module)
    publishLocaleTerms()
  }) ?? Promise.resolve()
}

function getCookieDomain() {
  const arr = window.location.hostname.split('.')
  return arr.length >= 2 ? `.${arr[arr.length - 2]}.${arr[arr.length - 1]}` : arr[0]
}

let localeRequest = 0

export async function setLocale(next) {
  const request = ++localeRequest
  if (!supportedLocales.includes(next) || next === i18n.global.locale.value) {
    return
  }

  await loadLocaleMessages(next)
  if (request !== localeRequest) {
    return
  }

  const applyLocale = () => {
    Cookies.set('zendrop-lang', next, {
      expires: 365,
      domain: getCookieDomain(),
    })
    i18n.global.locale.value = next
    document.documentElement.lang = next

    EventBusV3.emit(LOCALE_CHANGED, next)
    return nextTick()
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!document.startViewTransition || prefersReducedMotion) {
    await applyLocale()
    return
  }

  const root = document.documentElement
  root.classList.add('locale-transition')
  const transition = document.startViewTransition(applyLocale)
  await transition.finished.finally(() => {
    if (request === localeRequest) {
      root.classList.remove('locale-transition')
    }
  })
}

if (locale !== 'en' && !published[locale]) {
  loadLocaleMessages(locale)
}

export default i18n
